import { Injectable } from '@angular/core';
import { StorageHelper } from './helpers/StorageHelper';

export type ThemeMode = 'auto' | 'dark' | 'light';

@Injectable()
export class ThemeService {

  listeners: ThemeListener[] = [];

  constructor() {
    this.migrateOldDarkThemeKey();
    this.registerOsThemeListener();
  }

  getThemeMode(): ThemeMode {
    const stored = StorageHelper.getInstance().get(StorageHelper.keys.themeMode);
    if (stored === 'dark' || stored === 'light' || stored === 'auto') {
      return stored;
    }
    return 'auto';
  }

  setThemeMode(mode: ThemeMode) {
    StorageHelper.getInstance().save(StorageHelper.keys.themeMode, mode);
    this.applyTheme();
  }

  isDarkTheme(): boolean {
    if (typeof document === 'undefined' || !document.body) {
      return this.resolveEffectiveDark();
    }
    return document.body.className === 'theme-dark';
  }

  applyTheme() {
    const dark = this.resolveEffectiveDark();
    if (typeof document !== 'undefined' && document.body) {
      document.body.className = dark ? 'theme-dark' : 'theme-light';
    }
    for (const l of this.listeners) {
      l.themeUpdated();
    }
  }

  updateTheme(value?: boolean) {
    if (value != null) {
      if (typeof document !== 'undefined' && document.body) {
        document.body.className = value ? 'theme-dark' : 'theme-light';
      }
      for (const l of this.listeners) {
        l.themeUpdated();
      }
    } else {
      this.applyTheme();
    }
  }

  setDarkTheme(value: boolean) {
    this.setThemeMode(value ? 'dark' : 'light');
  }

  addListener(listener: ThemeListener) {
    this.listeners.push(listener);
  }

  removeListener(listener: ThemeListener) {
    const index = this.listeners.indexOf(listener);
    if (index !== -1) {
      this.listeners.splice(index, 1);
    }
  }

  private resolveEffectiveDark(): boolean {
    const mode = this.getThemeMode();
    if (mode === 'dark') { return true; }
    if (mode === 'light') { return false; }
    return this.detectAutoDarkTheme();
  }

  private detectAutoDarkTheme(): boolean {
    try {
      if (window.matchMedia) {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        if (mq.media !== 'not all') {
          return mq.matches;
        }
      }
    } catch (e) { }
    const hour = new Date().getHours();
    return hour >= 20 || hour < 7;
  }

  private migrateOldDarkThemeKey() {
    const oldValue = StorageHelper.getInstance().get(StorageHelper.keys.darkTheme);
    const hasNewKey = StorageHelper.getInstance().get(StorageHelper.keys.themeMode);
    if (oldValue !== null && oldValue !== undefined && (hasNewKey === null || hasNewKey === undefined)) {
      const newMode: ThemeMode = oldValue === 'true' ? 'dark' : 'auto';
      StorageHelper.getInstance().save(StorageHelper.keys.themeMode, newMode);
    }
  }

  private registerOsThemeListener() {
    try {
      if (!window.matchMedia) { return; }
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => {
        if (this.getThemeMode() === 'auto') {
          this.applyTheme();
        }
      };
      if ((mq as any).addEventListener) {
        (mq as any).addEventListener('change', handler);
      } else if ((mq as any).addListener) {
        (mq as any).addListener(handler);
      }
    } catch (e) { }
  }

}

export interface ThemeListener {
  themeUpdated();
}
